import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  getUserDrawerSections,
  userFormInitialValues
} from '@common/constants';
import {
  useCheckUserByPerson,
  useCreateUser,
  useGetBusinessUserLovs,
  useGetPersonDetailsById,
  useGetPersonEmailDetailsInfo,
  useUpdateUser
} from '@common/slices';
import { EnhancedButtonStatus } from '@components/button/EnhancedButton';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';
import DateService from '@core/services/DateService';
import { userLookup } from '@utils';
import { isEmpty } from '@utils/validationUtils';

interface IUserDrawer {
  open: boolean;
  onClose: () => void;
  userDetailsInfo?: any;
  userId?: string;
}

const UserDrawer: React.FC<IUserDrawer> = ({
  open,
  onClose,
  userDetailsInfo,
  userId
}) => {
  const navigate = useNavigate();
  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();

  const [defaultValues, setDefaultValues] = useState<any>();

  const [lookups, setLookups] = useState<
    Record<string, Record<string, string>>
  >({});

  const [inputsForm, setInputsForm] = useState<IDynamicFormWidgetSection[]>(
    getUserDrawerSections(
      userId,
      defaultValues,
      lookups,
      defaultValues?.newPerson || false
    )
  );

  const [booted, setBooted] = useState<boolean>(false);

  const { data: userLovData, loading } = useGetBusinessUserLovs();
  const [personDetails] = useGetPersonDetailsById();
  const [personEmailDetails] = useGetPersonEmailDetailsInfo();
  const [checkBusinessUserByPerson] = useCheckUserByPerson();
  const [updateUser] = useUpdateUser(userId);
  const [createUser] = useCreateUser();

  const initialize = async () => {
    const lookupLists = userLookup(userLovData);
    setLookups(lookupLists);

    setInputsForm(
      getUserDrawerSections(
        userId,
        userDetailsInfo,
        lookupLists,
        userDetailsInfo?.newPerson || false
      )
    );
    setBooted(true);
  };

  useEffect(() => {
    if (!loading) initialize();
  }, [loading]);

  const validateBusinessUserExistsOnPersonChange = async (
    selectedPersonID: string
  ) => {
    try {
      if (!isEmpty(selectedPersonID)) {
        const result = await checkBusinessUserByPerson({
          variables: {
            selectedPersonID: selectedPersonID
          }
        });

        if (
          result.data.SalesforceManagement?.queries?.checkBusinessUserByPerson
            .length > 0
        ) {
          return `Business User already created for 
            ${result.data.SalesforceManagement?.queries?.checkBusinessUserByPerson[0]?.salesforceManagement_BusinessUser_PersonID?.views?.SalesforceManagement_all?.properties?.PersonCode}
            - 
            ${result.data.SalesforceManagement?.queries?.checkBusinessUserByPerson[0]?.salesforceManagement_BusinessUser_PersonID?.views?.SalesforceManagement_all?.properties?.FullName}`;
        }
      }
    } catch (err) {
      //
    }

    return '';
  };

  const getPersonEmailDetails = async (
    selectedPersonID: string
  ): Promise<string> => {
    try {
      if (!isEmpty(selectedPersonID)) {
        const result = await personEmailDetails({
          variables: {
            id: selectedPersonID
          }
        });

        return (
          result.data?.SalesforceManagement?.entities?.person?.views
            ?.SalesforceManagement_all?.properties?.Email || ''
        );
      }
    } catch (err) {
      //
    }

    return '';
  };

  const submitForm = async (values: Record<string, any>) => {
    const [data] = normalizeDynamicFormDrawerValues(
      getUserDrawerSections(userId, defaultValues, lookups, values.newPerson),
      values
    );

    setFormDisabled(true);
    setSubmitButtonState('loading');
    const addressObj = {
      country: data.country ?? null,
      city: data.city ?? null,
      street: data.street ?? null,
      zone: data.zone ?? null,
      building: data.building ?? null,
      addressDetails: data.addressDetails ?? null,
      addressDetailsArabic: data.addressDetailsArabic ?? null
    };
    if (userId) {
      await updateUser({
        userInput: {
          email: data.email,
          password: data.password || null,
          access: data.access,
          role: data.role,
          type: data.type,
          firstName: data.firstName || null,
          lastName: data.lastName || null,
          mobileNumber: data.mobileNumber || null,
          middleName: data.middleName || null,
          // dateOfBirth:
          //   DateService.formatDateTimeRangeBackend(data.dateOfBirth) || null,
          dateOfBirth: data.dateOfBirth
            ? DateService.formatDateBackend(data.dateOfBirth)
            : null,
          title: data.title || data.user?.title || '',
          newPerson: data.newPerson,
          personId: data.personId,
          addressType: data.addressType || null,
          status: data.status,
          address: addressObj
          // personID: data.person,
          // password: data.password || null,
          // type: data.type,
          // account: data.account || null,
          // access: data.access,
          // status: data.SalesforceManagement_BusinessUserStatuses,
          // isAdmin: data.isAdmin,
        }
      });
    } else {
      await createUser({
        businessUserInputs: {
          personId: userId,
          access: data.access,
          email: data.email || null,
          firstName: data.firstName || null,
          lastName: data.lastName || null,
          middleName: data.middleName || null,
          type: data.type,
          mobileNumber: data.mobileNumber || null,
          role: data.role || null,
          status: data.status,
          newPerson: data.newPerson,
          title: data.title || data.user?.title || ''
        }
      });
    }
    setFormDisabled(false);
    setSubmitButtonState(undefined);
  };

  const onCustomChange = async (
    fieldName: string,
    value: any,
    sectionIndex: number,
    allValues: Record<string, any>,
    errors: Record<string, any>,
    touched: Record<string, boolean>
  ) => {
    allValues[fieldName] = value;
    if (fieldName === 'person') {
      const [detailsResult, businessUserValidation] = await Promise.all([
        personDetails({ variables: { personId: value } }),
        validateBusinessUserExistsOnPersonChange(value)
      ]);

      const personData = detailsResult?.data?.salesforce?.getPersonById;

      allValues.user = {
        email: personData?.email || '',
        phoneNumber: personData?.phoneDetails?.phoneNumber || '',
        firstName: personData?.firstName || '',
        lastName: personData?.lastName || '',
        middleName: personData?.middleName || '',
        title: personData?.title || '',
        dateOfBirth: personData?.dateOfBirth
          ? DateService.formatDateBackend(personData?.dateOfBirth)
          : null
      };

      allValues.personId = value;

      errors.person = businessUserValidation || '';
      touched.person = true;

      const mergedValues = {
        ...defaultValues,
        ...allValues,
        user: {
          ...allValues.user
        }
      };

      setInputsForm(
        getUserDrawerSections(
          userId,
          mergedValues,
          lookups,
          mergedValues.newPerson || false
        )
      );

      setDefaultValues(mergedValues as any);
    }
    if (fieldName === 'country') {
      const newLookups = { ...lookups };

      if (value === 'LB') {
        // keep zones lookup as is
      } else {
        newLookups.zone = {};
      }

      allValues.zone = value === 'LB' ? allValues.zone : null;

      setLookups(newLookups);

      const mergedValues = {
        ...defaultValues,
        ...allValues,
        user: {
          email: allValues.email ?? '',
          password: allValues.password ?? '',
          firstName: allValues.firstName ?? '',
          middleName: allValues.middleName ?? '',
          lastName: allValues.lastName ?? '',
          phoneNumber: allValues.mobileNumber ?? '',
          access: allValues.access ?? '',
          type: allValues.type ?? '',
          userRoles: allValues.role ? [{ role: { name: allValues.role } }] : []
        }
      };

      setInputsForm(
        getUserDrawerSections(
          userId,
          mergedValues,
          newLookups,
          mergedValues.newPerson || false
        )
      );

      setDefaultValues(mergedValues);
    }

    if (fieldName === 'access') {
      allValues.isAdmin = false;
      const reshapedValues = {
        ...defaultValues,
        ...allValues,
        user: {
          email: allValues.email ?? '',
          password: allValues.password ?? '',
          firstName: allValues.firstName ?? '',
          middleName: allValues.middleName ?? '',
          lastName: allValues.lastName ?? '',
          phoneNumber: allValues.mobileNumber ?? '',
          access: value,
          role: '',
          type: allValues.type ?? '',
          userRoles: allValues.role ? [{ role: { name: '' } }] : []
        }
      };

      const updatedSections = getUserDrawerSections(
        userId,
        reshapedValues,
        lookups,
        reshapedValues.newPerson || false
      );

      setInputsForm(updatedSections);
      setDefaultValues(reshapedValues);
    }

    if (fieldName === 'newPerson') {
      const sections = getUserDrawerSections(
        userId,
        userFormInitialValues(value === true),
        lookups,
        value === true
      );
      setInputsForm(sections);
      setDefaultValues({ ...userFormInitialValues(value === true) });
    }
  };

  return (
    <DynamicFormDrawer
      title={userId ? 'Modify User' : 'New User'}
      loading={!booted}
      onClose={() => onClose()}
      isOpen={open}
      sections={inputsForm}
      onSubmit={(values) => submitForm(values)}
      submitButtonState={submitButtonState}
      disableForm={formDisabled}
      onChange={onCustomChange}
    />
  );
};

export { UserDrawer };
